<template>
  <div>
    <!-- Property Amalgamation Modal -->
    <div class="modal fade property-amalgamation-modal" tabindex="-1" role="dialog" aria-labelledby="amalgamationModalLabel"
         aria-hidden="true">
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-uppercase" id="amalgamationModalLabel">Property Amalgamation</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Step Indicator -->
            <div class="row mb-4">
              <div class="col-12">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="d-flex flex-column align-items-center" v-for="(step, index) in steps" :key="index">
                    <div class="rounded-circle d-flex justify-content-center align-items-center"
                         :class="[currentStep >= index ? 'bg-primary text-white' : 'bg-light text-muted']"
                         style="width: 40px; height: 40px;">
                      {{ index + 1 }}
                    </div>
                    <span class="mt-2 text-center" :class="[currentStep >= index ? 'text-primary' : 'text-muted']">{{ step }}</span>
                  </div>
                </div>
                <div class="progress mt-3" style="height: 2px;">
                  <div class="progress-bar bg-primary" role="progressbar"
                       :style="{ width: (currentStep / (steps.length - 1) * 100) + '%' }"
                       aria-valuenow="50" aria-valuemin="0" aria-valuemax="100"></div>
                </div>
              </div>
            </div>

            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Processing...</p>
            </div>

            <!-- Step 1: Select Properties with Balance Check -->
            <div v-if="!loading && currentStep === 0" class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Select the properties you want to amalgamate. All selected properties must be in the same sub-county, have 'Active' status, and have zero outstanding balance.</p>
                </div>
              </div>

              <!-- Outstanding Balance Warning -->
              <div v-if="propertiesWithBalance.length > 0" class="col-12 mb-3">
                <div class="alert alert-warning">
                  <h5 class="alert-heading">
                    <i class="bx bx-error-circle me-2"></i>
                    Properties with Outstanding Balances
                  </h5>
                  <p><strong>The following properties cannot be amalgamated due to outstanding balances:</strong></p>
                  <div class="table-responsive">
                    <table class="table table-sm table-bordered">
                      <thead>
                      <tr>
                        <th>LR Number</th>
                        <th>Owner</th>
                        <th>Outstanding Balance</th>
                        <th>Status</th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-for="property in propertiesWithBalance" :key="property.id">
                        <td>{{ property.LRNumber }}</td>
                        <td>{{ property.ownerNames }}</td>
                        <td class="text-danger">KES {{ numberFormat(property.currentBalance) }}</td>
                        <td><span class="badge bg-warning">Blocked</span></td>
                      </tr>
                      </tbody>
                    </table>
                  </div>
                  <p class="mb-0"><strong>Action Required:</strong> Clear all outstanding balances before attempting amalgamation.</p>
                </div>
              </div>

              <div class="col-12 mb-3">
                <div class="form-group">
                  <label class="form-label">Search Properties (by LR Number or Owner Name)</label>
                  <div class="input-group">
                    <input type="text" v-model="searchKeyword" class="form-control" placeholder="Enter LR Number or Owner Name">
                    <button @click="searchProperties" class="btn btn-primary" type="button">
                      <i class="mdi mdi-magnify"></i> Search
                    </button>
                  </div>
                </div>
              </div>

              <div class="col-12">
                <div v-if="loadingSearch" class="text-center py-3">
                  <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                  <p class="mt-2">Searching properties...</p>
                </div>

                <div v-else-if="eligibleProperties.length === 0 && !loadingSearch" class="alert alert-warning">
                  <p>No eligible properties found. Properties must have zero outstanding balance and Active status.</p>
                </div>

                <div v-else class="table-responsive">
                  <table class="table table-bordered table-hover">
                    <thead class="table-light">
                    <tr>
                      <th style="width: 50px;"></th>
                      <th>LR Number</th>
                      <th>Owner</th>
                      <th>Percel Size</th>
                      <th>Current Balance</th>
                      <th>Address</th>
                      <th>Sub County</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="property in eligibleProperties" :key="property.id">
                      <td>
                        <div class="form-check">
                          <input class="form-check-input" type="checkbox" :value="property.id"
                                 v-model="selectedPropertyIds"
                                 :disabled="isPropertyInDifferentSubCounty(property) || hasOutstandingBalance(property)">
                        </div>
                      </td>
                      <td>{{ property.LRNumber }}</td>
                      <td>{{ property.ownerNames }}</td>
                      <td>{{ property.percelSize ? `${property.percelSize} Ha` : 'N/A' }}</td>
                      <td>
                        <span v-if="hasOutstandingBalance(property)" class="text-danger fw-bold">
                          KES {{ numberFormat(property.currentBalance) }}
                        </span>
                        <span v-else class="text-success">KES 0.00</span>
                      </td>
                      <td>{{ property.percelAddress }}</td>
                      <td>{{ property.subCountyName }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>

                <div v-if="selectedPropertyIds.length > 0" class="mt-3">
                  <p><strong>Selected Properties:</strong> {{ selectedPropertyIds.length }}</p>
                  <p><strong>Total Area:</strong> {{ calculateTotalArea() }} Ha</p>
                </div>
              </div>
            </div>

            <!-- Step 2: New Property Details -->
            <div v-if="!loading && currentStep === 1" class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Enter the details for the new amalgamated property.</p>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">New LR Number <span class="text-danger">*</span></label>
                  <input type="text" v-model="newProperty.LRNumber" class="form-control" required>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Percel Size (Ha) <span class="text-danger">*</span></label>
                  <input
                      type="number"
                      v-model="newProperty.percelSize"
                      class="form-control"
                      step="0.01"
                      required
                      @input="updatePercelSize"
                  >
                  <small class="text-muted">Suggested: {{ calculateTotalArea() }} Ha (based on selected properties)</small>
                </div>
              </div>

              <div class="col-12 mb-3">
                <div class="form-group">
                  <label class="form-label">Physical Address <span class="text-danger">*</span></label>
                  <input type="text" v-model="newProperty.percelAddress" class="form-control" required>
                </div>
              </div>

              <!-- Enhanced financial fields -->
              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Annual Land Rates</label>
                  <input type="number" v-model="newProperty.annualLandRates" class="form-control" step="0.01">
                  <small class="text-muted">Suggested: {{ calculateTotalAnnualLandRates() }}</small>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Annual Ground Rent</label>
                  <input type="number" v-model="newProperty.annualGroundRent" class="form-control" step="0.01">
                  <small class="text-muted">Suggested: {{ calculateTotalAnnualGroundRent() }}</small>
                </div>
              </div>
            </div>

            <!-- Step 3: Owner Details -->
            <div v-if="!loading && currentStep === 2" class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Select the owner for the new amalgamated property.</p>
                </div>
              </div>

              <div class="col-12 mb-3">
                <div class="form-group">
                  <label class="form-label">Owner Selection</label>
                  <select v-model="ownerSelection" class="form-select" @change="handleOwnerSelectionChange">
                    <option value="existing">Use Existing Owner</option>
                    <option value="new">Enter New Owner</option>
                  </select>
                </div>
              </div>

              <div v-if="ownerSelection === 'existing'" class="col-12 mb-3">
                <div class="form-group">
                  <label class="form-label">Select From Existing Owners</label>
                  <select v-model="selectedOwnerIndex" class="form-select" @change="selectExistingOwner">
                    <option v-for="(property, index) in selectedProperties" :key="index" :value="index">
                      {{ property.ownerNames }} ({{ property.LRNumber }})
                    </option>
                  </select>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Owner Name <span class="text-danger">*</span></label>
                  <input type="text" v-model="newProperty.ownerDetails.ownerNames" class="form-control" required>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Email</label>
                  <input type="email" v-model="newProperty.ownerDetails.ownerEmail" class="form-control">
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Phone Number <span class="text-danger">*</span></label>
                  <input type="text" v-model="newProperty.ownerDetails.ownerPhoneNo" class="form-control" required>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">ID Number</label>
                  <input type="text" v-model="newProperty.ownerDetails.ownerID" class="form-control">
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">PIN Number</label>
                  <input type="text" v-model="newProperty.ownerDetails.pinNumber" class="form-control">
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="form-group">
                  <label class="form-label">Physical Address</label>
                  <input type="text" v-model="newProperty.ownerDetails.physicalAddress" class="form-control">
                </div>
              </div>
            </div>

            <!-- Step 4: Documentation -->
            <div v-if="!loading && currentStep === 3" class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Upload supporting documents for the amalgamation and add any additional notes.</p>
                </div>
              </div>

              <!-- Document Upload Section -->
              <div class="col-12 mb-4">
                <div class="card border-primary">
                  <div class="card-header bg-primary text-white">
                    <h6 class="mb-0 text-white">
                      <i class="bx bx-file-plus me-2"></i>
                      Amalgamation Documentation
                    </h6>
                  </div>
                  <div class="card-body">
                    <div class="mb-3">
                      <label class="form-label">Upload Supporting Documents</label>
                      <p class="text-muted small">Upload amalgamation approval documents, survey plans, or other relevant documentation (PDF, DOC, DOCX, JPG, PNG - Max 10MB per file)</p>
                      <input
                          ref="fileInput"
                          type="file"
                          class="form-control"
                          multiple
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          @change="handleFileUpload"
                      >
                    </div>

                    <div v-if="uploadedFiles.length > 0" class="mb-3">
                      <label class="form-label">Uploaded Documents:</label>
                      <div class="uploaded-files">
                        <div v-for="(file, index) in uploadedFiles" :key="index" class="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                          <div class="d-flex align-items-center">
                            <i class="bx bx-file me-2 text-primary"></i>
                            <div>
                              <div class="fw-semibold">{{ file.name }}</div>
                              <small class="text-muted">{{ formatFileSize(file.size) }}</small>
                            </div>
                          </div>
                          <button
                              @click="removeFile(index)"
                              type="button"
                              class="btn btn-sm btn-outline-danger"
                          >
                            <i class="bx bx-trash"></i>
                          </button>
                        </div>
                      </div>
                    </div>

                    <div class="mb-3">
                      <label for="amalgamationNotes" class="form-label">Additional Notes</label>
                      <textarea
                          v-model="amalgamationNotes"
                          id="amalgamationNotes"
                          class="form-control"
                          rows="3"
                          placeholder="Enter any additional notes or comments about the amalgamation..."
                      ></textarea>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 5: Confirmation -->
            <div v-if="!loading && currentStep === 4" class="row">
              <div class="col-12 mb-3">
                <div class="alert alert-warning">
                  <i class="mdi mdi-alert-outline me-2"></i>
                  <p><strong>Important:</strong> Please review the details carefully. Once confirmed, the selected properties will be amalgamated into a new property, and the original properties will be marked as 'Amalgamated'.</p>
                </div>
              </div>

              <div class="col-12 mb-4">
                <h6 class="fw-bold">Selected Properties for Amalgamation:</h6>
                <div class="table-responsive">
                  <table class="table table-sm table-bordered">
                    <thead class="table-light">
                    <tr>
                      <th>LR Number</th>
                      <th>Owner</th>
                      <th>Percel Size</th>
                      <th>Balance Status</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="property in selectedProperties" :key="property.id">
                      <td>{{ property.LRNumber }}</td>
                      <td>{{ property.ownerNames }}</td>
                      <td>{{ property.percelSize }} Ha</td>
                      <td><span class="badge bg-success">Clear</span></td>
                    </tr>
                    </tbody>
                    <tfoot class="table-light">
                    <tr>
                      <td colspan="2" class="text-end fw-bold">Total Area:</td>
                      <td>{{ calculateTotalArea() }} Ha</td>
                      <td></td>
                    </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div class="col-12 mb-4">
                <h6 class="fw-bold">New Amalgamated Property:</h6>
                <div class="table-responsive">
                  <table class="table table-sm">
                    <tbody>
                    <tr>
                      <th style="width: 200px;">LR Number:</th>
                      <td>{{ newProperty.LRNumber }}</td>
                    </tr>
                    <tr>
                      <th>Percel Size:</th>
                      <td>{{ newProperty.percelSize }} Ha</td>
                    </tr>
                    <tr>
                      <th>Physical Address:</th>
                      <td>{{ newProperty.percelAddress }}</td>
                    </tr>
                    <tr>
                      <th>Annual Land Rates:</th>
                      <td>KES {{ numberFormat(newProperty.annualLandRates) }}</td>
                    </tr>
                    <tr>
                      <th>Annual Ground Rent:</th>
                      <td>KES {{ numberFormat(newProperty.annualGroundRent) }}</td>
                    </tr>
                    <tr>
                      <th>Owner:</th>
                      <td>{{ newProperty.ownerDetails.ownerNames }}</td>
                    </tr>
                    <tr>
                      <th>Contact:</th>
                      <td>{{ newProperty.ownerDetails.ownerPhoneNo }} / {{ newProperty.ownerDetails.ownerEmail }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- Documentation Summary -->
              <div v-if="uploadedFiles.length > 0 || amalgamationNotes" class="col-12 mb-3">
                <div class="card border-info">
                  <div class="card-header bg-info text-white">
                    <h6 class="mb-0 text-white">Documentation Summary</h6>
                  </div>
                  <div class="card-body">
                    <div v-if="uploadedFiles.length > 0" class="mb-3">
                      <strong>Uploaded Documents ({{ uploadedFiles.length }}):</strong>
                      <ul class="mt-2 mb-0">
                        <li v-for="file in uploadedFiles" :key="file.name">{{ file.name }}</li>
                      </ul>
                    </div>
                    <div v-if="amalgamationNotes">
                      <strong>Notes:</strong>
                      <p class="mt-2 mb-0">{{ amalgamationNotes }}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Step 6: Success -->
            <div v-if="!loading && currentStep === 5" class="row">
              <div class="col-12 text-center mb-4">
                <div class="d-flex justify-content-center">
                  <div class="avatar-sm rounded-circle bg-success">
                    <span class="avatar-title rounded-circle bg-success">
                      <i class="mdi mdi-check-all font-size-24 text-white"></i>
                    </span>
                  </div>
                </div>
                <h5 class="mt-4">Amalgamation Completed Successfully!</h5>
                <p class="text-muted">Properties have been amalgamated into a new property with LR Number: <strong>{{ newProperty.LRNumber }}</strong></p>
              </div>

              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <p class="mb-1"><i class="mdi mdi-information-outline me-1"></i> Amalgamation Reference: <strong>{{ amalgamationResult.amalgamationReference }}</strong></p>
                  <div v-if="uploadedFiles.length > 0" class="mt-2">
                    <i class="bx bx-info-circle me-2"></i>
                    {{ uploadedFiles.length }} document(s) have been successfully uploaded and linked to this amalgamation.
                  </div>
                  <p class="mb-0 mt-2">You can now view the details of the new property, or close this dialog to return to the property list.</p>
                </div>
              </div>

              <div class="col-12 text-center">
                <button @click="viewNewPropertyDetails" class="btn btn-primary" data-bs-dismiss="modal">
                  <i class="mdi mdi-eye me-1"></i> View New Property
                </button>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button v-if="currentStep > 0 && currentStep < 5" type="button" class="btn btn-light" @click="prevStep">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
            <button v-if="currentStep < 4" type="button" class="btn btn-primary" @click="nextStep" :disabled="!canProceed">
              Next <i class="mdi mdi-arrow-right ms-1"></i>
            </button>
            <button v-if="currentStep === 4" type="button" class="btn btn-success" @click="amalgamateProperties" :disabled="loading">
              <i class="mdi mdi-check me-1"></i> Confirm Amalgamation
            </button>
            <button v-if="currentStep === 5" type="button" class="btn btn-secondary" data-bs-dismiss="modal">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Amalgamation History Modal (unchanged) -->
    <div class="modal fade amalgamation-history-modal" tabindex="-1" role="dialog" aria-labelledby="amalgamationHistoryModalLabel"
         aria-hidden="true">
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title text-uppercase" id="amalgamationHistoryModalLabel">Amalgamation History</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <p class="mt-3">Loading amalgamation history...</p>
            </div>

            <div v-else>
              <div class="col-12 mb-3">
                <div class="alert alert-info">
                  <i class="mdi mdi-information-outline me-2"></i>
                  <p>Amalgamation history for property <strong>{{ property.LRNumber }}</strong>.</p>
                </div>
              </div>

              <div v-if="isNewAmalgamatedProperty" class="mb-4">
                <h6 class="fw-bold">New Amalgamated Property</h6>
                <p>This property was created through amalgamation of the following properties:</p>
                <div class="table-responsive">
                  <table class="table table-bordered table-striped">
                    <thead>
                    <tr>
                      <th>Original LR Number</th>
                      <th>Percel Size</th>
                      <th>Amalgamation Date</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(record, index) in amalgamationHistory.originalProperties" :key="index">
                      <td>{{ record.original_lr_number }}</td>
                      <td>{{ record.percel_size }} Ha</td>
                      <td>{{ formatDate(record.amalgamation_date) }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="isPartOfAmalgamation" class="mb-4">
                <h6 class="fw-bold">Part of Amalgamation</h6>
                <p>This property was amalgamated into a new property:</p>
                <div class="table-responsive">
                  <table class="table table-bordered">
                    <thead>
                    <tr>
                      <th>New LR Number</th>
                      <th>Amalgamation Reference</th>
                      <th>Amalgamation Date</th>
                      <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr>
                      <td>{{ amalgamationHistory.newProperty.new_lr_number }}</td>
                      <td>{{ amalgamationHistory.newProperty.amalgamation_reference }}</td>
                      <td>{{ formatDate(amalgamationHistory.newProperty.amalgamation_date) }}</td>
                      <td>
                        <button @click="viewProperty(amalgamationHistory.newProperty.new_property_id)" class="btn btn-sm btn-primary">
                          <i class="mdi mdi-eye"></i> View
                        </button>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-if="!isNewAmalgamatedProperty && !isPartOfAmalgamation" class="text-center py-3">
                <p>No amalgamation history found for this property.</p>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { landRate, execute } from '@/api';
import moment from 'moment';

export default {
  name: 'PropertyAmalgamation',
  props: {
    property: {
      type: Object,
      required: true
    }
  },
  data() {
    return {
      steps: ['Select Properties', 'Property Details', 'Owner Details', 'Documentation', 'Confirmation', 'Complete'],
      currentStep: 0,
      loading: false,
      loadingSearch: false,
      availableProperties: [],
      eligibleProperties: [],
      propertiesWithBalance: [],
      selectedPropertyIds: [],
      selectedProperties: [],
      searchKeyword: '',
      ownerSelection: 'existing',
      selectedOwnerIndex: 0,
      uploadedFiles: [],
      amalgamationNotes: '',
      maxFileSize: 10 * 1024 * 1024, // 10MB
      newProperty: {
        LRNumber: '',
        percelSize: 0,
        percelAddress: '',
        annualLandRates: 0,
        annualGroundRent: 0,
        ownerDetails: {
          ownerNames: '',
          ownerEmail: '',
          ownerPhoneNo: '',
          ownerID: '',
          pinNumber: '',
          physicalAddress: ''
        }
      },
      amalgamationResult: {
        amalgamationReference: '',
        newPropertyId: '',
        newLRNumber: ''
      },
      amalgamationHistory: {
        originalProperties: [],
        newProperty: null
      },
      isNewAmalgamatedProperty: false,
      isPartOfAmalgamation: false
    };
  },
  computed: {
    canProceed() {
      switch (this.currentStep) {
        case 0:
          return this.selectedPropertyIds.length >= 2;
        case 1:
          return this.newProperty.LRNumber &&
              this.newProperty.percelSize > 0 &&
              this.newProperty.percelAddress;
        case 2:
          return this.newProperty.ownerDetails.ownerNames &&
              this.newProperty.ownerDetails.ownerPhoneNo;
        case 3:
          return true; // Documentation step is optional
        case 4:
          return true; // Confirmation step
        default:
          return true;
      }
    }
  },
  watch: {
    selectedPropertyIds: {
      handler(newIds) {
        // Clear and refill selectedProperties array based on ids
        this.selectedProperties = [];
        for (const id of newIds) {
          const property = this.eligibleProperties.find(p => p.id == id);
          if (property) {
            this.selectedProperties.push(property);
          }
        }

        // Calculate the total area from all selected properties
        const totalArea = this.calculateTotalArea();

        // Always update the percel size to reflect the total area of selected properties
        this.newProperty.percelSize = totalArea;

        // Calculate total financial values
        this.newProperty.annualLandRates = this.calculateTotalAnnualLandRates();
        this.newProperty.annualGroundRent = this.calculateTotalAnnualGroundRent();

        // If this is the first selection, suggest using the first property's address
        if (this.selectedProperties.length > 0 && !this.newProperty.percelAddress) {
          this.newProperty.percelAddress = this.selectedProperties[0].percelAddress;
        }
      },
      deep: true
    }
  },
  methods: {
    hasOutstandingBalance(property) {
      return (property.currentBalance || 0) > 0;
    },

    async openModal() {
      // Reset form data
      this.currentStep = 0;
      this.selectedPropertyIds = [];
      this.selectedProperties = [];
      this.uploadedFiles = [];
      this.amalgamationNotes = '';
      this.newProperty = {
        LRNumber: '',
        percelSize: 0,
        percelAddress: '',
        annualLandRates: 0,
        annualGroundRent: 0,
        ownerDetails: {
          ownerNames: '',
          ownerEmail: '',
          ownerPhoneNo: '',
          ownerID: '',
          pinNumber: '',
          physicalAddress: ''
        }
      };

      // Fetch available properties
      await this.searchProperties();
    },

    async searchProperties() {
      this.loadingSearch = true;
      this.eligibleProperties = [];
      this.propertiesWithBalance = [];

      const data = new FormData();
      data.append('function', 'getPropertiesForAmalgamation');
      data.append('subCountyName', this.property.subCountyName);
      data.append('search', this.searchKeyword);

      try {
        const response = await execute(data, landRate);
        if (response.data.success && response.data.data && response.data.data.properties) {
          this.availableProperties = response.data.data.properties;

          // Remove the current property from the list if it's included
          if (this.property && this.property.id) {
            this.availableProperties = this.availableProperties.filter(p => p.id !== this.property.id);
          }

          // Add the current property to the beginning of the list if it's Active and has no balance
          if (this.property && this.property.status === 'Active') {
            this.availableProperties.unshift(this.property);
          }

          // Separate properties with and without outstanding balances
          this.availableProperties.forEach(property => {
            if (this.hasOutstandingBalance(property)) {
              this.propertiesWithBalance.push(property);
            } else if (property.status === 'Active') {
              this.eligibleProperties.push(property);
            }
          });
        }
      } catch (error) {
        console.error('Error fetching properties:', error);
        this.eligibleProperties = [];
        this.propertiesWithBalance = [];
      } finally {
        this.loadingSearch = false;
      }
    },

    isPropertyInDifferentSubCounty(property) {
      if (this.selectedPropertyIds.length === 0) return false;

      const firstSelectedProperty = this.eligibleProperties.find(p => p.id == this.selectedPropertyIds[0]);
      return firstSelectedProperty && property.subCountyName !== firstSelectedProperty.subCountyName;
    },

    calculateTotalArea() {
      const total = this.selectedProperties.reduce((sum, property) => {
        const size = parseFloat(property.percelSize) || 0;
        return sum + size;
      }, 0);
      return parseFloat(total.toFixed(2));
    },

    calculateTotalAnnualLandRates() {
      const total = this.selectedProperties.reduce((sum, property) => {
        return sum + parseFloat(property.annualLandRates || 0);
      }, 0);
      return parseFloat(total.toFixed(2));
    },

    calculateTotalAnnualGroundRent() {
      const total = this.selectedProperties.reduce((sum, property) => {
        return sum + parseFloat(property.annualGroundRent || 0);
      }, 0);
      return parseFloat(total.toFixed(2));
    },

    // File handling methods
    handleFileUpload(event) {
      const files = Array.from(event.target.files);
      const validFiles = [];

      for (const file of files) {
        // Validate file size
        if (file.size > this.maxFileSize) {
          alert(`File "${file.name}" is too large. Maximum size is 10MB.`);
          continue;
        }

        // Validate file type
        const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
          alert(`File "${file.name}" is not a supported format. Please upload PDF, DOC, DOCX, JPG, or PNG files.`);
          continue;
        }

        validFiles.push(file);
      }

      this.uploadedFiles.push(...validFiles);

      // Clear the input
      event.target.value = '';
    },

    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    updatePercelSize(event) {
      this.newProperty.percelSize = parseFloat(event.target.value) || 0;
    },

    handleOwnerSelectionChange() {
      if (this.ownerSelection === 'existing' && this.selectedProperties.length > 0) {
        this.selectExistingOwner();
      } else if (this.ownerSelection === 'new') {
        // Clear owner details for new input
        this.newProperty.ownerDetails = {
          ownerNames: '',
          ownerEmail: '',
          ownerPhoneNo: '',
          ownerID: '',
          pinNumber: '',
          physicalAddress: ''
        };
      }
    },

    selectExistingOwner() {
      if (this.selectedOwnerIndex >= 0 && this.selectedOwnerIndex < this.selectedProperties.length) {
        const selectedProperty = this.selectedProperties[this.selectedOwnerIndex];
        this.newProperty.ownerDetails = {
          ownerNames: selectedProperty.ownerNames || '',
          ownerEmail: selectedProperty.ownerEmail || '',
          ownerPhoneNo: selectedProperty.ownerPhoneNo || '',
          ownerID: selectedProperty.ownerID || '',
          pinNumber: selectedProperty.pinNumber || '',
          physicalAddress: selectedProperty.physicalAddress || ''
        };
      }
    },

    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },

    nextStep() {
      if (this.currentStep < this.steps.length - 1 && this.canProceed) {
        this.currentStep++;

        // If moving to confirmation step, refresh calculations
        if (this.currentStep === 4) {
          this.newProperty.percelSize = this.calculateTotalArea();
          this.newProperty.annualLandRates = this.calculateTotalAnnualLandRates();
          this.newProperty.annualGroundRent = this.calculateTotalAnnualGroundRent();
        }
      }
    },

    async amalgamateProperties() {
      if (!confirm('Are you sure you want to amalgamate these properties? This action cannot be undone.')) {
        return;
      }

      this.loading = true;

      // Create form data
      const formData = new FormData();
      formData.append('function', 'amalgamateProperties');
      formData.append('propertyIds', JSON.stringify(this.selectedPropertyIds));
      formData.append('newLRNumber', this.newProperty.LRNumber);
      formData.append('newPercelSize', this.newProperty.percelSize);
      formData.append('newPercelAddress', this.newProperty.percelAddress);
      formData.append('ownerDetails', JSON.stringify(this.newProperty.ownerDetails));
      formData.append('annualLandRates', this.newProperty.annualLandRates);
      formData.append('annualGroundRent', this.newProperty.annualGroundRent);
      formData.append('amalgamationNotes', this.amalgamationNotes);
      formData.append('sessionNames', sessionStorage.getItem('sessionNames'));
      formData.append('sessionIdNo', sessionStorage.getItem('sessionIdNo'));

      // Append files
      this.uploadedFiles.forEach((file, index) => {
        formData.append(`amalgamationDoc_${index}`, file);
      });
      formData.append('totalFiles', this.uploadedFiles.length.toString());

      try {
        // Fix: Use formData instead of data
        const response = await execute(formData, landRate);

        if (response.data.success) {
          this.amalgamationResult = response.data.data;
          this.currentStep = 5; // Move to success step
          this.$emit('refresh'); // Tell parent component to refresh the property list
        } else {
          alert(response.data.message || 'Amalgamation failed. Please try again.');
        }
      } catch (error) {
        console.error('Error during amalgamation:', error);
        alert('An error occurred during amalgamation. Please try again.');
      } finally {
        this.loading = false;
      }
    },

    viewNewPropertyDetails() {
      this.$emit('viewProperty', this.amalgamationResult.newPropertyId);
    },

    // History methods (unchanged)
    async openAmalgamationHistory(propertyId, LRNumber) {
      this.loading = true;
      this.amalgamationHistory = {
        originalProperties: [],
        newProperty: null
      };
      this.isNewAmalgamatedProperty = false;
      this.isPartOfAmalgamation = false;

      const data = new FormData();
      data.append('function', 'getAmalgamationHistory');
      data.append('propertyId', propertyId || this.property.id);
      data.append('LRNumber', LRNumber || this.property.LRNumber);

      try {
        const response = await execute(data, landRate);

        if (response.data.success) {
          const historyData = response.data.data.amalgamationHistory;

          const isNewProperty = historyData.some(item => item.new_property_id == this.property.id);
          const wasPartOfAmalgamation = historyData.some(item => item.original_property_id == this.property.id);

          if (isNewProperty) {
            this.isNewAmalgamatedProperty = true;
            this.amalgamationHistory.originalProperties = historyData.filter(
                item => item.new_property_id == this.property.id
            );
          }

          if (wasPartOfAmalgamation) {
            this.isPartOfAmalgamation = true;
            this.amalgamationHistory.newProperty = historyData.find(
                item => item.original_property_id == this.property.id
            );
          }
        }
      } catch (error) {
        console.error('Error fetching amalgamation history:', error);
      } finally {
        this.loading = false;
      }
    },

    viewProperty(propertyId) {
      this.$emit('viewProperty', propertyId);
    },

    formatDate(date) {
      return moment(date).format('DD MMM YYYY');
    },

    numberFormat(num) {
      return new Intl.NumberFormat('en-GB').format(num || 0);
    }
  }
}
</script>

<style scoped>
.progress-bar {
  transition: width 0.3s ease;
}

.uploaded-files {
  max-height: 200px;
  overflow-y: auto;
}

.card-header.bg-primary {
  background-color: #007bff !important;
}

.card-header.bg-info {
  background-color: #17a2b8 !important;
}
</style>